import NiceModal from '@ebay/nice-modal-react';

import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import LordIcon from 'components/global/LordIcon';
import Popper from 'components/global/Popper';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useDebounce from 'hooks/useDebounce';
import useLocalStorage from 'hooks/useLocalStorage';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { getOTOPackageList } from 'pages/OTOModules/OTOPackageListSlice';
import SubscriptionAccountModal from 'pages/Settings/Payment/SubscriptionAccount/SubscriptionAccountModal';

import { toogleSideBar } from 'stores/layoutSlice';

import { THEME } from 'utils/Theme';
import { PLATFORM } from 'utils/constants';
import PATHS from 'utils/paths';

import { faAngleDown, faBarsSort, faChevronLeft, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';

import Profile from './Profile';
import * as S from './SideBar.styled';
import { settingsSidebarConfig, sideBarConfig } from './SideBarConfig';
import SideBarLink from './SideBarLink';
import SwitchCustomer from './SwitchCustomer';

/**
 * @author Burak Unsal
 */

const SideBar = () => {
	const [closedCategories, setClosedCategories] = useLocalStorage('closedSidebarCategories', [
		'setup',
		'customerNotification',
		'imports',
		'importOrdersHeader',
		'performance',
		'customerNotifs',
		'integrations',
		'otoPack',
		'otoFlex',
		'management',
		'payments',
		'dataCenter',
		'advanced',
		'developers',
		'logs'
	]);
	const [lastSwitchUsers] = useLocalStorage('lastSwitchUsers', []);
	const [supportUserName, setSupportUserName] = useLocalStorage('supportUserName', null);

	const { pathname } = useLocation();
	const dispatch = useDispatch();
	const isAuthorized = useAccessControl();
	const navigate = useNavigate();
	const { otoPackageList: packages } = useSelector((state) => state?.otoPackageList);
	const { sideBarOpen, darkMode } = useSelector((state) => state.layout);
	const user = useSelector((state) => state.user);

	const [profileButtonRef, setProfileButtonRef] = useState(null);
	const [switchButtonRef, setSwitchButtonRef] = useState(null);
	const [currentPackage, setCurrentPackage] = useState({});

	const findIsPathSettingsLink = (menuList = settingsSidebarConfig) => {
		return menuList.reduce((found, item) => {
			if (found) return found;
			if (item.links && item.links.length) {
				return findIsPathSettingsLink(item.links);
			}

			return !item.external && item.path.split('/')[1] === pathname.split('/')[1];
		}, false);
	};
	const [isOpenSettings, setIsOpenSettings] = useState(findIsPathSettingsLink());
	const debouncedIsOpenSettings = useDebounce(isOpenSettings);

	useEffect(() => {
		if (user.accountSuspended && !user?.hasUnpaidSubscription) {
			dispatch(getOTOPackageList());
		}
	}, []);

	useEffect(() => {
		setCurrentPackage(packages?.find((item) => item.planName === user.currentPlanName));
	}, [packages, user.currentPlanName]);

	const settingsItems = settingsSidebarConfig.map((item, index) => {
		let renderThis = false;

		item.links.forEach((link) => {
			if (isAuthorized(link.path)) {
				renderThis = true;
			}
		});

		if (!renderThis) {
			return;
		}

		return (
			<Fragment key={index}>
				<SideBarLink
					key={index}
					item={item}
					closedCategories={closedCategories}
					setClosedCategories={setClosedCategories}
				/>
				{!sideBarOpen && !closedCategories.includes(item.header) && <S.Separator />}
			</Fragment>
		);
	});

	const isSettingsEmpty = settingsItems?.filter((item) => !!item).length === 0;

	useEffect(() => {
		if (user?.isSupportUser && user.ID !== lastSwitchUsers[0]?.clientUserId) {
			setSupportUserName(user.fullName);
		}
	}, [user?.isSupportUser, lastSwitchUsers]);

	useUpdateEffect(() => {
		const isPathSettingsLink = findIsPathSettingsLink();
		if (isPathSettingsLink && !isOpenSettings) {
			setIsOpenSettings(true);
		}

		// Auto-open category when redirecting to a page
		const updateCategoriesForCurrentPath = () => {
			const firstPathSegment = pathname.split('/')[1];
			const configArray = isPathSettingsLink ? settingsSidebarConfig : sideBarConfig;
			// First check in settings sidebar config
			for (const category of configArray) {
				for (const link of category.links) {
					if (link.path.split('/')[1] === firstPathSegment && closedCategories.includes(category.header)) {
						// Remove the category from closedCategories
						setClosedCategories(closedCategories.filter((item) => item !== category.header));
						return;
					}
				}
			}
		};

		updateCategoriesForCurrentPath();
	}, [pathname]);

	return (
		<S.Sidebar id="sidebar" className={sideBarOpen ? 'sidebar-open' : ''} isopen={sideBarOpen}>
			<S.SidebarHeader isopen={sideBarOpen} isSettingsEmpty={isSettingsEmpty}>
				<S.SidebarFlex isopen={sideBarOpen}>
					<S.SidebarLogo>
						<Image
							alt="logo"
							url={'https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png'}
							onClick={() => navigate(PATHS.HOME)}
						/>
						{import.meta.env.VITE_PLATFORM === PLATFORM.desktop && sideBarOpen && (
							<Badge variant="pill" size="small" color="red" text="tag.beta" />
						)}
					</S.SidebarLogo>
					<Button
						id="sidebarToggleButton"
						className="sidebarToggleIcon"
						variant="borderless"
						iconButton
						onClick={() => dispatch(toogleSideBar())}>
						<Icon icon={faBarsSort} />
					</Button>
				</S.SidebarFlex>
				{isOpenSettings && (
					<S.SettingsHeader>
						<Button
							icon={faChevronLeft}
							variant="blueBorderless"
							label={sideBarOpen && 'backToHome'}
							onClick={() => setIsOpenSettings(false)}
							className="settings-btn"
							fullWidth
							css={S.SettingsButtonCss}
							iconButton={!sideBarOpen}
						/>
						{sideBarOpen && (
							<Typography variant="p1" fontWeight="700" noWrap>
								settings
							</Typography>
						)}
					</S.SettingsHeader>
				)}
			</S.SidebarHeader>

			<S.SidebarBodyContainer>
				<S.SidebarBodyWrapper className={`${isOpenSettings ? 'slide-right' : ''}`} isopen={sideBarOpen}>
					<S.SidebarBody isopen={sideBarOpen}>
						{(!isOpenSettings || debouncedIsOpenSettings !== isOpenSettings) &&
							sideBarConfig.map((item, index) => {
								let renderThis = false;

								item.links.forEach((link) => {
									if (isAuthorized(link.path)) {
										renderThis = true;
									}
								});

								if (!renderThis) {
									return;
								}

								return (
									<Fragment key={index}>
										<SideBarLink
											key={index}
											item={item}
											closedCategories={closedCategories}
											setClosedCategories={setClosedCategories}
										/>
										{!sideBarOpen && !closedCategories.includes(item.header) && <S.Separator />}
									</Fragment>
								);
							})}
					</S.SidebarBody>
					<S.SidebarBody isopen={sideBarOpen}>
						{(isOpenSettings || debouncedIsOpenSettings !== isOpenSettings) && settingsItems}
					</S.SidebarBody>
				</S.SidebarBodyWrapper>
			</S.SidebarBodyContainer>

			<S.SidebarFooter isopen={sideBarOpen}>
				{user?.hasUnpaidSubscription === true && (
					<Alert
						className="mb-3"
						variant="warning"
						content="subscriptionRenewalFailed"
						ctaButtonLabel="renew"
						onCtaButtonClick={() =>
							NiceModal.show(SubscriptionAccountModal, {
								renewSubscription: true
							})
						}
						showIcon
					/>
				)}

				{user.accountSuspended && !user?.hasUnpaidSubscription && (
					<Alert
						className="mb-3"
						variant="error"
						content="subscriptionSuspendedTitle"
						ctaButtonLabel="renew"
						onCtaButtonClick={() =>
							NiceModal.show(SubscriptionAccountModal, {
								currentPackage,
								selectedPackage: packages.find((item) => item.planName === user.planName),
								selectedPlanType: 'MONTHLY'
							})
						}
						showIcon
					/>
				)}
				{!isOpenSettings && (
					<S.SettingsContainer isOpen={sideBarOpen}>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/configuration-icon.json.gz"
							variant="borderless"
							label="settings"
							onClick={() => setIsOpenSettings(true)}
							className="settings-btn"
							fullWidth
							iconButton={!sideBarOpen}
							css={S.LinkButtonCss({
								isopen: sideBarOpen
							})}
						/>
					</S.SettingsContainer>
				)}

				<Authorization path={PATHS.OTO_MODULES}>
					<Tooltip title={!sideBarOpen && 'otoModules'}>
						<Button
							icon={faGrid2Plus}
							variant="blue"
							id="otoModuleButton"
							css={S.UpgradeButtonCss}
							onClick={() => navigate(PATHS.OTO_MODULES)}
							fullWidth
							iconSize="large">
							{sideBarOpen && (
								<S.UpgradeButtonTextContainer isOpen={sideBarOpen}>
									<S.BadgeContainer>
										<div>
											<Typography color="var(--alwaysWhiteColor)" fontWeight="500">
												otoModules
											</Typography>
											<Badge text="new" variant="pill" size="small" color="red" />
										</div>
										<Typography color="var(--alwaysWhiteColor)" variant="caption">
											{user.planName}
										</Typography>
									</S.BadgeContainer>
								</S.UpgradeButtonTextContainer>
							)}
						</Button>
					</Tooltip>
				</Authorization>

				{user?.isSupportUser && (
					<Button
						ref={setSwitchButtonRef}
						size="small"
						css={S.ProfileButtonCss}
						variant="borderless"
						fullWidth>
						<div className="profile">
							<LordIcon
								primaryColor={darkMode ? THEME.WHITE : THEME.BLACK}
								secondaryColor={darkMode ? THEME.DARK.PRIMARY_COLOR : THEME.LIGHT.PRIMARY_COLOR}
								width={29}
								height={29}
								src="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/customerService-icon.json.gz"
								style={{
									backgroundColor:
										user.ID === lastSwitchUsers[0]?.clientUserId
											? 'var(--green)'
											: 'var(--getSupportImageBackgroundColor)',
									borderRadius: '50%'
								}}
							/>
							{sideBarOpen && (
								<div className="text">
									<Typography
										fontWeight={500}
										color={
											user.ID === lastSwitchUsers[0]?.clientUserId ? 'var(--green)' : undefined
										}>
										{user.ID === lastSwitchUsers[0]?.clientUserId
											? supportUserName
											: 'selectClientUser'}
									</Typography>
									<Typography
										color={
											user.ID === lastSwitchUsers[0]?.clientUserId ? 'var(--green)' : undefined
										}>
										{user.ID === lastSwitchUsers[0]?.clientUserId
											? 'loggedinAsClient'
											: 'loginAsClient'}
									</Typography>
								</div>
							)}
						</div>
						{sideBarOpen && <Icon icon={faAngleDown} />}
					</Button>
				)}

				<Button ref={setProfileButtonRef} size="small" css={S.ProfileButtonCss} variant="borderless" fullWidth>
					<div className="profile">
						<Image
							url={user.avatarPath}
							alt="logo"
							defaultUrl="https://storage.googleapis.com/tryoto-public/public/assets/images/otonoavatar.png"
							width="3.2rem"
							height="3.2rem"
						/>

						{sideBarOpen && (
							<div className="text">
								<Typography fontWeight={500}>
									{user.lastName ? `${user.firstName} ${user.lastName}` : user.fullName}
								</Typography>
								<Typography>{user.userType}</Typography>
							</div>
						)}
					</div>
					{sideBarOpen && <Icon icon={faAngleDown} />}
				</Button>

				{switchButtonRef && (
					<Popper
						wrapperId="SwitchPopper"
						refElement={switchButtonRef}
						placement="right-end"
						containerCss={S.PopperCss}
						offsetY={0}
						hoverMode>
						{(closePopper) => <SwitchCustomer closePopper={closePopper} />}
					</Popper>
				)}

				{profileButtonRef && (
					<Popper
						wrapperId="ProfilePopper"
						refElement={profileButtonRef}
						placement="right-end"
						containerCss={S.PopperCss}
						offsetY={0}
						closeTimeout={1000}
						hoverMode
						disableMobileView>
						{(closePopper) => <Profile closePopper={closePopper} />}
					</Popper>
				)}
			</S.SidebarFooter>
		</S.Sidebar>
	);
};

export default SideBar;
